import { useState } from "react";
import { Calendar as CalendarIcon } from "lucide-react";
import { format } from "date-fns";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { calculateDaysUntil } from "@/lib/utils";
import GooglePlacesAutocomplete from "@/components/GooglePlacesAutocomplete";

export const CreateWeddingDialog = ({ open, onOpenChange, onCreateWedding }) => {
  const [formData, setFormData] = useState({
    brideName: "",
    groomName: "",
    date: undefined,
    venue: null,
    budget: ""
  });
  const [calendarOpen, setCalendarOpen] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Validation - venue is now optional
    if (!formData.brideName || !formData.groomName || !formData.date || !formData.budget) {
      toast.error("Please fill in bride name, groom name, date, and budget");
      return;
    }

    const newWedding = {
      bride_name: formData.brideName,
      groom_name: formData.groomName,
      date: format(formData.date, "yyyy-MM-dd"),
      venue: formData.venue?.name || '',
      budget: parseInt(formData.budget),
      guest_count: 0
    };

    onCreateWedding(newWedding);
    toast.success(`Wedding for ${formData.brideName} & ${formData.groomName} created successfully!`);
    
    // Reset form
    setFormData({
      brideName: "",
      groomName: "",
      date: undefined,
      venue: null,
      budget: ""
    });
    onOpenChange(false);
  };

  const handleDateSelect = (date) => {
    setFormData({ ...formData, date });
    setCalendarOpen(false); // Auto-close calendar
  };

  const handleVenueChange = (place) => {
    setFormData({ ...formData, venue: place });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px] max-h-[85vh] flex flex-col">
        <DialogHeader className="flex-shrink-0">
          <DialogTitle className="text-2xl font-bold gradient-text">Create New Wedding</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="flex flex-col flex-1 min-h-0">
          <div className="space-y-3 overflow-y-auto pr-2 flex-1">
            <div>
              <Label htmlFor="brideName">Bride's Name</Label>
              <Input
                id="brideName"
                value={formData.brideName}
                onChange={(e) => setFormData({ ...formData, brideName: e.target.value })}
                placeholder="Enter bride's name"
                className="mt-1.5"
              />
            </div>
            
            <div>
              <Label htmlFor="groomName">Groom's Name</Label>
              <Input
                id="groomName"
                value={formData.groomName}
                onChange={(e) => setFormData({ ...formData, groomName: e.target.value })}
                placeholder="Enter groom's name"
                className="mt-1.5"
              />
            </div>

            <div>
              <Label>Wedding Date</Label>
              <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn(
                      "w-full justify-start text-left font-normal mt-1.5",
                      !formData.date && "text-muted-foreground"
                    )}
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {formData.date ? format(formData.date, "PPP") : "Pick a date"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={formData.date}
                    onSelect={handleDateSelect}
                    defaultMonth={formData.date || new Date()}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>

            <div>
              <Label htmlFor="venue">Venue</Label>
              <div className="mt-1.5">
                <GooglePlacesAutocomplete
                  value={formData.venue}
                  onChange={handleVenueChange}
                  placeholder="Search for venue location..."
                  types={['establishment']}
                />
              </div>
            </div>

            <div>
              <Label htmlFor="budget">Budget (₹)</Label>
              <Input
                id="budget"
                type="number"
                value={formData.budget}
                onChange={(e) => setFormData({ ...formData, budget: e.target.value })}
                placeholder="Enter total budget"
                className="mt-1.5"
              />
            </div>
          </div>

          <DialogFooter className="flex-shrink-0 mt-4 pt-4 border-t">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" className="gradient-primary">
              Create Wedding
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default CreateWeddingDialog;
